import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/state/auth";
import { P, type Permission } from "@/lib/permissions";
import { Shell } from "@/components/Shell";
import { Spinner } from "@/components/ui";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { Dashboard } from "@/pages/Dashboard";
import { Book } from "@/pages/Book";
import { GuestBook } from "@/pages/GuestBook";
import { GuestStatus } from "@/pages/GuestStatus";
import { Checkout } from "@/pages/Checkout";
import { Shipments } from "@/pages/Shipments";
import { ShipmentDetail } from "@/pages/ShipmentDetail";
import { Addresses } from "@/pages/Addresses";
import { ApiKeys } from "@/pages/ApiKeys";
import { Webhooks } from "@/pages/Webhooks";
import { Settings } from "@/pages/Settings";
import { Team } from "@/pages/Team";
import { Billing } from "@/pages/Billing";
import { Wallet } from "@/pages/Wallet";
import { Communications } from "@/pages/Communications";
import { RateCalculator } from "@/pages/RateCalculator";
import { Track } from "@/pages/Track";
import { Privacy } from "@/pages/Privacy";
import { Terms } from "@/pages/Terms";
import { Refunds } from "@/pages/Refunds";
import { Prohibited } from "@/pages/Prohibited";
import { NotFound } from "@/pages/NotFound";
import { Forbidden } from "@/pages/Forbidden";
import { PlatformDashboard } from "@/pages/admin/PlatformDashboard";
import { PlatformAccounts } from "@/pages/admin/PlatformAccounts";
import { Dispatch } from "@/pages/ops/Dispatch";
import { PlatformUsers } from "@/pages/admin/PlatformUsers";
import { PlatformRoles } from "@/pages/admin/PlatformRoles";
import { AccountWallet } from "@/pages/admin/AccountWallet";
import { PlatformSettings } from "@/pages/admin/PlatformSettings";
import { PlatformNotifications } from "@/pages/admin/PlatformNotifications";
import { AdminWebhooks } from "@/pages/admin/AdminWebhooks";
import { ServiceLevels } from "@/pages/admin/ServiceLevels";
import { RateZones } from "@/pages/admin/RateZones";
import { RateCards } from "@/pages/admin/RateCards";
import { PlatformPayments } from "@/pages/admin/PlatformPayments";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { SetPassword } from "@/pages/SetPassword";

function Protected({ children }: { children: React.ReactNode }) {
  const { account, ready } = useAuth();
  if (!ready) {
    return (
      <div className="grid min-h-dvh place-items-center text-ink-400">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  return account ? <>{children}</> : <Navigate to="/login" replace />;
}

/**
 * Route-level permission guard.
 *
 * The navigation already hides what a role cannot reach, but a hidden link is
 * not a permission check: people bookmark URLs and share them. This is the
 * second lock, and the API is the third.
 */
function Requires({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can, ready } = useAuth();
  if (!ready) return null;
  return can(permission) ? <>{children}</> : <Forbidden />;
}

/**
 * Where a session lands after signing in depends on the job.
 *
 * A dispatcher wants the board, a super admin wants the platform, a merchant
 * wants their own shipments, and a driver has no portal at all. Sending
 * everyone to the same page and making them navigate is a small tax paid on
 * every single login.
 */
function Home() {
  const { can } = useAuth();
  if (can(P.platformDashboard)) return <Navigate to="/app/admin" replace />;
  if (can(P.opsDashboard)) return <Navigate to="/app/ops" replace />;
  if (can(P.viewShipments)) return <Dashboard />;
  return <Forbidden />;
}

export default function App() {
  return (
    <AuthProvider>
      {/* Inside the router, above the routes: every navigation starts the new
          page at the top, except going back. */}
      <ScrollToTop />
      <Routes>
        {/* The landing page is public and stays public when signed in: a
            customer following a marketing link should not be bounced. */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Guest><Login /></Guest>} />
        <Route path="/register" element={<Guest><Register /></Guest>} />
        {/* Not wrapped in Guest: somebody already signed in on another tab
            may still be following a link to change their password. */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/track" element={<Track />} />
        <Route path="/track/:waybill" element={<Track />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/prohibited-items" element={<Prohibited />} />

        {/* Guest checkout: one shipment, no account, no login. */}
        <Route path="/ship" element={<GuestBook />} />
        <Route path="/ship/status/:paymentId" element={<GuestStatus />} />

        <Route path="/app" element={<Protected><Shell /></Protected>}>
          <Route index element={<Home />} />

          {/* Shipping */}
          <Route path="book" element={<Requires permission={P.book}><Book /></Requires>} />
          {/* Where the gateway returns a customer who paid the shortfall on a
              booking. The webhook books it; this page only waits for that. */}
          <Route path="checkout/:paymentId" element={<Requires permission={P.book}><Checkout /></Requires>} />
          <Route path="rates" element={<Requires permission={P.quote}><RateCalculator /></Requires>} />
          <Route path="shipments" element={<Requires permission={P.viewShipments}><Shipments /></Requires>} />
          <Route path="shipments/:id" element={<Requires permission={P.viewShipments}><ShipmentDetail /></Requires>} />
          <Route path="addresses" element={<Requires permission={P.viewAddresses}><Addresses /></Requires>} />
          <Route path="wallet" element={<Requires permission={P.viewBilling}><Wallet /></Requires>} />
          <Route path="billing" element={<Requires permission={P.viewBilling}><Billing /></Requires>} />
          <Route path="communications" element={<Requires permission={P.viewShipments}><Communications /></Requires>} />

          {/* Account administration */}
          <Route path="team" element={<Requires permission={P.viewUsers}><Team /></Requires>} />
          <Route path="api-keys" element={<Requires permission={P.manageApiKey}><ApiKeys /></Requires>} />
          <Route path="webhooks" element={<Requires permission={P.manageApiKey}><Webhooks /></Requires>} />
          <Route path="settings" element={<Requires permission={P.viewSettings}><Settings /></Requires>} />

          {/* Operations, WeShip staff */}
          <Route path="ops" element={<Requires permission={P.opsDashboard}><PlatformDashboard scope="ops" /></Requires>} />
          <Route path="ops/accounts" element={<Requires permission={P.opsAccounts}><PlatformAccounts /></Requires>} />
          <Route path="ops/dispatch" element={<Requires permission={P.opsFleet}><Dispatch /></Requires>} />

          {/* Platform administration */}
          <Route path="admin" element={<Requires permission={P.platformDashboard}><PlatformDashboard scope="platform" /></Requires>} />
          <Route path="admin/accounts" element={<Requires permission={P.platformAccounts}><PlatformAccounts /></Requires>} />
          <Route path="admin/users" element={<Requires permission={P.platformUsers}><PlatformUsers /></Requires>} />
          <Route path="admin/roles" element={<Requires permission={P.platformRoles}><PlatformRoles /></Requires>} />
          <Route path="admin/settings" element={<Requires permission={P.platformSettings}><PlatformSettings /></Requires>} />
          <Route path="admin/service-levels" element={<Requires permission={P.platformSettings}><ServiceLevels /></Requires>} />
          <Route path="admin/zones" element={<Requires permission={P.platformSettings}><RateZones /></Requires>} />
          <Route path="admin/rate-cards" element={<Requires permission={P.platformSettings}><RateCards /></Requires>} />
          <Route path="admin/payments" element={<Requires permission={P.platformBilling}><PlatformPayments /></Requires>} />
          <Route path="admin/notifications" element={<Requires permission={P.platformSettings}><PlatformNotifications /></Requires>} />
          <Route path="admin/webhooks" element={<Requires permission={P.platformSettings}><AdminWebhooks /></Requires>} />
          <Route path="admin/accounts/:id/wallet" element={<Requires permission={P.platformAccounts}><AccountWallet /></Requires>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function Guest({ children }: { children: React.ReactNode }) {
  const { account, ready } = useAuth();
  if (!ready) return null;
  return account ? <Navigate to="/app" replace /> : <>{children}</>;
}
